/**
 * A tiny, sandboxed expression evaluator.
 *
 * It exists so config-driven transformation ops (e.g. "Calculate new field") can accept
 * a user-authored formula like `row.price * row.qty` or `(a + b) / 2` WITHOUT resorting to
 * `eval()` / `new Function()`. Only a fixed grammar is supported — field references,
 * literals, arithmetic/comparison/logical operators, a ternary, and a small whitelist of
 * pure helper functions. There is no property assignment, no statement execution, no access
 * to globals, prototypes, `this`, or anything outside the scope object handed in.
 *
 * Raw JavaScript is still available to authors, but only through the clearly-labeled
 * "Advanced" script tab — never silently behind a config field.
 */

type Token =
  | { kind: 'num'; value: number }
  | { kind: 'str'; value: string }
  | { kind: 'ident'; value: string }
  | { kind: 'op'; value: string }
  | { kind: 'punc'; value: string };

const OPERATORS = [
  '===', '!==', '==', '!=', '<=', '>=', '&&', '||', '<', '>', '+', '-', '*', '/', '%', '!', '?', ':',
];

function tokenize(input: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  const isIdentStart = (c: string) => /[A-Za-z_$]/.test(c);
  const isIdentChar = (c: string) => /[A-Za-z0-9_$]/.test(c);
  const isDigit = (c: string) => /[0-9]/.test(c);

  while (i < input.length) {
    const c = input[i];

    if (/\s/.test(c)) { i++; continue; }

    // String literal (single or double quote)
    if (c === '"' || c === "'") {
      const quote = c;
      let str = '';
      i++;
      while (i < input.length && input[i] !== quote) {
        if (input[i] === '\\' && i + 1 < input.length) {
          const next = input[i + 1];
          str += next === 'n' ? '\n' : next === 't' ? '\t' : next;
          i += 2;
        } else {
          str += input[i];
          i++;
        }
      }
      if (input[i] !== quote) throw new Error('Unterminated string literal');
      i++;
      tokens.push({ kind: 'str', value: str });
      continue;
    }

    // Number literal
    if (isDigit(c) || (c === '.' && isDigit(input[i + 1]))) {
      let num = '';
      while (i < input.length && (isDigit(input[i]) || input[i] === '.')) {
        num += input[i];
        i++;
      }
      tokens.push({ kind: 'num', value: Number(num) });
      continue;
    }

    // Identifier / keyword
    if (isIdentStart(c)) {
      let id = '';
      while (i < input.length && isIdentChar(input[i])) {
        id += input[i];
        i++;
      }
      tokens.push({ kind: 'ident', value: id });
      continue;
    }

    // Punctuation
    if (c === '(' || c === ')' || c === '[' || c === ']' || c === '.' || c === ',') {
      tokens.push({ kind: 'punc', value: c });
      i++;
      continue;
    }

    // Operators (longest match first)
    const matched = OPERATORS.find((op) => input.startsWith(op, i));
    if (matched) {
      tokens.push({ kind: 'op', value: matched });
      i += matched.length;
      continue;
    }

    throw new Error(`Unexpected character '${c}' at position ${i}`);
  }

  return tokens;
}

// --- AST ---
type Node =
  | { t: 'num'; v: number }
  | { t: 'str'; v: string }
  | { t: 'bool'; v: boolean }
  | { t: 'null' }
  | { t: 'ident'; name: string }
  | { t: 'member'; obj: Node; prop: string }
  | { t: 'index'; obj: Node; index: Node }
  | { t: 'call'; callee: Node; args: Node[] }
  | { t: 'unary'; op: string; arg: Node }
  | { t: 'binary'; op: string; left: Node; right: Node }
  | { t: 'logical'; op: string; left: Node; right: Node }
  | { t: 'ternary'; test: Node; cons: Node; alt: Node };

class Parser {
  private pos = 0;
  constructor(private tokens: Token[]) {}

  private peek(): Token | undefined { return this.tokens[this.pos]; }
  private next(): Token | undefined { return this.tokens[this.pos++]; }

  private expectPunc(value: string) {
    const tok = this.next();
    if (!tok || tok.kind !== 'punc' || tok.value !== value) {
      throw new Error(`Expected '${value}'`);
    }
  }

  parse(): Node {
    const node = this.parseTernary();
    if (this.pos < this.tokens.length) throw new Error('Unexpected trailing tokens in expression');
    return node;
  }

  private parseTernary(): Node {
    const test = this.parseBinary(0);
    const tok = this.peek();
    if (tok && tok.kind === 'op' && tok.value === '?') {
      this.next();
      const cons = this.parseTernary();
      const colon = this.next();
      if (!colon || colon.kind !== 'op' || colon.value !== ':') throw new Error("Expected ':' in ternary");
      const alt = this.parseTernary();
      return { t: 'ternary', test, cons, alt };
    }
    return test;
  }

  // Precedence climbing for binary operators.
  private static PRECEDENCE: Record<string, number> = {
    '||': 1, '&&': 2,
    '==': 3, '!=': 3, '===': 3, '!==': 3,
    '<': 4, '>': 4, '<=': 4, '>=': 4,
    '+': 5, '-': 5,
    '*': 6, '/': 6, '%': 6,
  };

  private parseBinary(minPrec: number): Node {
    let left = this.parseUnary();
    for (;;) {
      const tok = this.peek();
      if (!tok || tok.kind !== 'op') break;
      const prec = Parser.PRECEDENCE[tok.value];
      if (prec === undefined || prec < minPrec) break;
      this.next();
      const right = this.parseBinary(prec + 1);
      const isLogical = tok.value === '&&' || tok.value === '||';
      left = isLogical
        ? { t: 'logical', op: tok.value, left, right }
        : { t: 'binary', op: tok.value, left, right };
    }
    return left;
  }

  private parseUnary(): Node {
    const tok = this.peek();
    if (tok && tok.kind === 'op' && (tok.value === '!' || tok.value === '-' || tok.value === '+')) {
      this.next();
      return { t: 'unary', op: tok.value, arg: this.parseUnary() };
    }
    return this.parsePostfix();
  }

  private parsePostfix(): Node {
    let node = this.parsePrimary();
    for (;;) {
      const tok = this.peek();
      if (tok && tok.kind === 'punc' && tok.value === '.') {
        this.next();
        const prop = this.next();
        if (!prop || prop.kind !== 'ident') throw new Error('Expected property name after "."');
        node = { t: 'member', obj: node, prop: prop.value };
      } else if (tok && tok.kind === 'punc' && tok.value === '[') {
        this.next();
        const index = this.parseTernary();
        this.expectPunc(']');
        node = { t: 'index', obj: node, index };
      } else if (tok && tok.kind === 'punc' && tok.value === '(') {
        this.next();
        const args: Node[] = [];
        if (!(this.peek()?.kind === 'punc' && this.peek()?.value === ')')) {
          args.push(this.parseTernary());
          while (this.peek()?.kind === 'punc' && this.peek()?.value === ',') {
            this.next();
            args.push(this.parseTernary());
          }
        }
        this.expectPunc(')');
        node = { t: 'call', callee: node, args };
      } else {
        break;
      }
    }
    return node;
  }

  private parsePrimary(): Node {
    const tok = this.next();
    if (!tok) throw new Error('Unexpected end of expression');

    if (tok.kind === 'num') return { t: 'num', v: tok.value };
    if (tok.kind === 'str') return { t: 'str', v: tok.value };
    if (tok.kind === 'ident') {
      if (tok.value === 'true') return { t: 'bool', v: true };
      if (tok.value === 'false') return { t: 'bool', v: false };
      if (tok.value === 'null') return { t: 'null' };
      return { t: 'ident', name: tok.value };
    }
    if (tok.kind === 'punc' && tok.value === '(') {
      const node = this.parseTernary();
      this.expectPunc(')');
      return node;
    }
    throw new Error(`Unexpected token '${tok.value}'`);
  }
}

// Whitelisted, pure helper values available to every expression.
const SAFE_GLOBALS: Record<string, any> = {
  Math: {
    min: Math.min, max: Math.max, round: Math.round, floor: Math.floor,
    ceil: Math.ceil, abs: Math.abs, sqrt: Math.sqrt, pow: Math.pow,
    PI: Math.PI, E: Math.E,
  },
  Number,
  String,
  Boolean,
  parseInt: (v: any, radix?: number) => parseInt(v, radix),
  parseFloat: (v: any) => parseFloat(v),
};

function evaluate(node: Node, scope: Record<string, any>): any {
  switch (node.t) {
    case 'num': return node.v;
    case 'str': return node.v;
    case 'bool': return node.v;
    case 'null': return null;
    case 'ident':
      if (Object.prototype.hasOwnProperty.call(scope, node.name)) return scope[node.name];
      if (Object.prototype.hasOwnProperty.call(SAFE_GLOBALS, node.name)) return SAFE_GLOBALS[node.name];
      return undefined;
    case 'member': {
      const obj = evaluate(node.obj, scope);
      if (obj == null) return undefined;
      if (node.prop === '__proto__' || node.prop === 'constructor' || node.prop === 'prototype') {
        throw new Error(`Access to '${node.prop}' is not allowed`);
      }
      return obj[node.prop];
    }
    case 'index': {
      const obj = evaluate(node.obj, scope);
      if (obj == null) return undefined;
      const key = evaluate(node.index, scope);
      if (key === '__proto__' || key === 'constructor' || key === 'prototype') {
        throw new Error(`Access to '${String(key)}' is not allowed`);
      }
      return obj[key];
    }
    case 'call': {
      // Only allow calling members of whitelisted objects or whitelisted top-level fns.
      const args = node.args.map((a) => evaluate(a, scope));
      let thisArg: any = undefined;
      let fn: any;
      if (node.callee.t === 'member') {
        thisArg = evaluate(node.callee.obj, scope);
        fn = thisArg == null ? undefined : thisArg[node.callee.prop];
      } else {
        fn = evaluate(node.callee, scope);
      }
      if (typeof fn !== 'function') throw new Error('Attempted to call a non-function');
      return fn.apply(thisArg, args);
    }
    case 'unary': {
      const v = evaluate(node.arg, scope);
      if (node.op === '!') return !v;
      if (node.op === '-') return -v;
      return +v;
    }
    case 'logical': {
      const left = evaluate(node.left, scope);
      if (node.op === '&&') return left && evaluate(node.right, scope);
      return left || evaluate(node.right, scope);
    }
    case 'binary': {
      const l = evaluate(node.left, scope);
      const r = evaluate(node.right, scope);
      switch (node.op) {
        case '+': return l + r;
        case '-': return l - r;
        case '*': return l * r;
        case '/': return l / r;
        case '%': return l % r;
        case '==': return l == r; // eslint-disable-line eqeqeq
        case '!=': return l != r; // eslint-disable-line eqeqeq
        case '===': return l === r;
        case '!==': return l !== r;
        case '<': return l < r;
        case '>': return l > r;
        case '<=': return l <= r;
        case '>=': return l >= r;
        default: throw new Error(`Unknown operator '${node.op}'`);
      }
    }
    case 'ternary':
      return evaluate(node.test, scope) ? evaluate(node.cons, scope) : evaluate(node.alt, scope);
  }
}

const compileCache = new Map<string, (scope: Record<string, any>) => any>();

/**
 * Compile an expression string into a reusable `(scope) => value` function.
 * Throws on invalid syntax. Results are cached by expression text.
 */
export function compileExpression(expression: string): (scope: Record<string, any>) => any {
  const cached = compileCache.get(expression);
  if (cached) return cached;
  const ast = new Parser(tokenize(expression)).parse();
  const fn = (scope: Record<string, any>) => evaluate(ast, scope);
  compileCache.set(expression, fn);
  return fn;
}

/** Evaluate an expression once against a scope. Returns `undefined` on error (never throws). */
export function evaluateExpression(expression: string, scope: Record<string, any>): any {
  try {
    return compileExpression(expression)(scope);
  } catch (e) {
    console.warn('Expression evaluation failed:', expression, e);
    return undefined;
  }
}

/** Validate expression syntax without evaluating. Returns an error message, or null if valid. */
export function validateExpression(expression: string): string | null {
  try {
    new Parser(tokenize(expression)).parse();
    return null;
  } catch (e: any) {
    return e?.message || 'Invalid expression';
  }
}
